import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AuthActions } from '../redux-store/thunks/auth.thunks';
import { UserProfileActions } from '../redux-store/thunks/user-profile.thunks';
import { apologise } from '../redux-store/thunks/apology.thunks';
import {
  darkModeActivated,
  darkModeDeactivated,
  GlobalActions,
  portalJwtUpdated,
} from '../redux-store/thunks/global.thunks';
import { JwtAPI } from '../api/jwt.api';

import { useIsLiveUpdatesEnabled } from '../hooks/useIsLiveUpdatesEnabled.hook';
import { useRefreshJwtOnConnect } from '../hooks/useRefreshJwtOnConnect.hook';

import { UserMenu } from '../components/user-menu/UserMenu.view';
import { UserMenuToggler } from 'src/components/user-menu/UserMenuToggler.view';

import type { RootState } from '../types/state.types';

export const UserMenuContainer: React.FC = () => {
  const dispatch = useDispatch();

  const subId = useSelector((state: RootState) => state.subscription.id);
  const connected = useSelector((state: RootState) => state.subscription.connected);
  const portalUrl = useSelector((state: RootState) => state.globals.portalUrl);
  const darkModeActive = useSelector((state: RootState) => state.theme.dark);
  const userName = useSelector((state: RootState) => state.user.name);
  const email = useSelector((state: RootState) => state.user.email);
  const isPortalAdmin = useSelector((state: RootState) =>
    state.user.isPortalAdmin ? true : false
  );
  const canTogglePortalAdminPowers = useSelector((state: RootState) =>
    state.user.supportedOperations &&
    state.user.supportedOperations.activate_portal_admin_powers &&
    state.user.supportedOperations.activate_portal_admin_powers.enabled
      ? true
      : false
  );

  const isLiveUpdatesEnabled = useIsLiveUpdatesEnabled(subId);

  const setPortalAdminPowerActivationState = async (activate: boolean) => {
    await dispatch(UserProfileActions.changeProfile(null, { isPortalAdmin: activate }));

    if (subId) {
      try {
        const token = await JwtAPI.get(portalUrl, subId);
        dispatch(portalJwtUpdated(token));
      } catch (error) {
        dispatch(apologise(String(error)));
      }
    }
  };

  useRefreshJwtOnConnect({ subId, connected, isPortalAdmin, portalUrl });

  const toggleTheme = (isChecked: boolean) => {
    isChecked ? dispatch(darkModeActivated()) : dispatch(darkModeDeactivated());
  };

  const toggleEntitiesLiveUpdates = (isChecked: boolean) => {
    isChecked
      ? dispatch(GlobalActions.enableLiveUpdatesGlobally(subId))
      : dispatch(GlobalActions.disableLiveUpdatesGlobally(subId));
  };

  const logout = () => dispatch(AuthActions.logout());

  return (
    <UserMenuToggler darkModeActive={darkModeActive} email={email} userName={userName}>
      <UserMenu
        canTogglePortalAdminPowers={canTogglePortalAdminPowers}
        isPortalAdmin={isPortalAdmin}
        isLiveUpdatesEnabled={isLiveUpdatesEnabled}
        darkModeActive={darkModeActive}
        userName={userName}
        email={email}
        logout={logout}
        setPortalAdminPowerActivationState={setPortalAdminPowerActivationState}
        toggleEntitiesLiveUpdates={toggleEntitiesLiveUpdates}
        toggleTheme={toggleTheme}
      />
    </UserMenuToggler>
  );
};
